import React, {useState} from 'react';
import './configurator.scss';
import {
    IconAr,
    IconBrush,
    IconSnapshot,
    NfcScanner,
    IconCardDispenser,
    IconKeyboard,
    IconLightbox,
    ThermoPrinter,
    PrinterShredder,
    IconUpload,
    IconScreenPrivacyFilter
} from "./icons/svgs";
import RenderControlButton from "./buttons/render-control-button";
import ColorLookup from "./lookup/color-lookup";
import {IModelConfiguration} from '../model.schema';
import {formatPrice} from "../services/price-service";
import ModelViewer from './model-viewer';
import {useAppDispatch, useAppSelector} from '../redux/store';
import * as appStorage from '../redux/storages/app-storage'
import * as cnfStorage from '../redux/storages/configuration-storage'
import HighResPopupContainer from './popup/high-res-popup-container';
import ArPopupContainer from './popup/ar-popup-container';
import {useTrans} from "../redux/storages/i18n-storage";
import ColorPickerPopupContainer from "./popup/color-picker-popup-container";
// import ColorPalettePopupContainer from "./popup/color-palette-popup-container";
import {useMaterialOptions} from "../hooks/use-material-options";
import ViewerMenu from "./viewer-menu";
import AnimatedHide, {Direction} from "./animated-hide";
import BooleanLookup from "./lookup/boolean-lookup";
// import SwitchLookup from "./lookup/switch-lookup";
import AppLayout from "./app-layout";
import {AnnotationConfigName} from "../services/viewer-service";
import LightBoxPopupContainer from "./popup/light-box-popup-container";
import ConfigurationMenu from "./configuration-menu";
import {useAppContext} from "../hooks/use-app-context";
import classNames from "classnames";
import DebugPopupContainer from "./popup/debug-popup-container";
import StartDebugButton from "./buttons/start-debug-button";
import ShareLinkButton from './share-link-button';

enum Popup {
    'none',
    'highRes',
    'color',
    'ar',
    'debug'
}

const isLayoutOrientationHorizontal = false
const moreThan5MenuButtons = true //update breakpoint if > 6

interface IProps {
    configuration?: IModelConfiguration,
    mutate: (lookupId: string, value: any) => void,
}

interface PopupProps {
    dimensions: {
        width: number,
        height: number,
    }
}

const Configurator: React.FC<IProps> = ({configuration, mutate}) => {

    const isDevelopoment = 'on' === String(process.env.REACT_APP_CL__DEBUG_ENABLE_THREE_FILE_UPLOAD).toLowerCase();
    const dispatch = useAppDispatch();
    const [popup, setPopup] = useState<Popup>(Popup.none);
    const zoomed = useAppSelector(appStorage.selectors.zoomed);
    const selectedZoom = useAppSelector(appStorage.selectors.getSelectedZoom)
    const zoom = (zoom: AnnotationConfigName | undefined) => dispatch(appStorage.actions.zoom(zoom));
    const price = useAppSelector(cnfStorage.selectors.getTotalPrice);
    const t = useTrans();
    const currentMaterial = useAppSelector(cnfStorage.selectors.currentMaterial);
    const materialOptions = useMaterialOptions(configuration);
    const context = useAppContext();

    const renderPopups = ({dimensions}: PopupProps) => {
        return <>
            {
                popup === Popup.color && configuration &&
                <ColorPickerPopupContainer configuration={configuration} onClose={() => setPopup(Popup.none)}/>
                // <ColorPalettePopupContainer configuration={configuration} onClose={() => setPopup(Popup.none)}/>
            }
            {
                popup === Popup.highRes &&
                <HighResPopupContainer dimensions={dimensions} onClose={() => setPopup(Popup.none)}/>
            }
            {
                popup === Popup.ar && <ArPopupContainer onClose={() => setPopup(Popup.none)}/>
            }
            {
                popup === Popup.debug && <DebugPopupContainer onClose={() => setPopup(Popup.none)}/>
            }
            {AnnotationConfigName.zoomPosLight === selectedZoom && (
                <LightBoxPopupContainer
                    onClose={() => {
                        zoom(undefined)
                    }}
                />
            )}
        </>
    }

    const renderViewport = () => {
        return <>
            <ModelViewer horizontal={isLayoutOrientationHorizontal}/>
            <AnimatedHide
                hidden={zoomed || popup === Popup.highRes || popup === Popup.ar}
                direction={isLayoutOrientationHorizontal ? Direction.top : Direction.left}
            >
                <ViewerMenu
                    horizontal={isLayoutOrientationHorizontal}
                    className="cl-configurator-bottomLeftSpacer"
                >
                    <RenderControlButton
                      onClick={() => {
                          setPopup(Popup.ar)
                      }}
                      title={t("viewport.control.button.arview.text")}
                      isSelected={false}
                    >
                        <IconAr/>
                    </RenderControlButton>
                    <RenderControlButton
                        onClick={() => {
                            setPopup(Popup.highRes)
                        }}
                        title={t("viewport.control.button.snapshot.text")}
                        isSelected={false}
                    >
                        <IconSnapshot/>
                    </RenderControlButton>
                    <ShareLinkButton/>
                </ViewerMenu>
            </AnimatedHide>
        </>
    }

    const configurationAreaClasses = classNames(
        "cl-configuration-area ",
        moreThan5MenuButtons ? "cl-configuration-area-shrink" : ""
    )

    const renderConfiguration = () => {
        return <div className={configurationAreaClasses}>
            {
                isDevelopoment && <StartDebugButton
                    onClick={() => {
                        setPopup(Popup.debug)
                    }}
                    title={t("debug.start.button.text")}
                >
                    <IconUpload/>
                </StartDebugButton>
            }
            <div className="cl-configuration-head">
                {
                    context && (
                        <div className="cl-configuration-mainHeader">
                            {t(context.titleLangId)}
                        </div>
                    )
                }
            </div>
        </div>
    }

    return (
        <AppLayout
            renderPopups={renderPopups}
            renderViewport={renderViewport}
            renderConfiguration={renderConfiguration}
            enableMobileScrolling={context?.enableMobileScrolling}
        />
    );
}

export default Configurator